import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';

import {
  activeUsersQuery,
  analyticsMetricQuery,
  DEFAULT_ANALYTICS_RANGE,
  sessionDurationQuery,
  type AnalyticsRange,
} from '@/entities/analytics';
import { useCurrentWorkspaceId } from '@/entities/workspace';

import {
  average,
  averageOverActiveDays,
  formatCount,
  formatDecimal,
  sum,
  toPoints,
  toStackedPoints,
} from '../../lib/series';
import { MetricCard } from '../MetricCard';
import { MetricChart } from '../MetricChart';
import { RangeToggle } from '../RangeToggle';

/** True when every value is zero - a series of zeros is "no activity", not data. */
const hasNoActivity = (values: number[]): boolean => values.every((value) => value === 0);

export const AnalyticsDashboard = () => {
  const { t } = useTranslation('common');
  const [range, setRange] = useState<AnalyticsRange>(DEFAULT_ANALYTICS_RANGE);
  // The workspace never travels in the request - an interceptor attaches the
  // header - but it scopes the query cache, so the queries stay disabled until
  // it is known.
  const workspaceId = useCurrentWorkspaceId();

  const activeUsers = useQuery(activeUsersQuery(workspaceId, range));
  const sessions = useQuery(analyticsMetricQuery(workspaceId, 'agent_sessions', range));
  const messagesSent = useQuery(analyticsMetricQuery(workspaceId, 'messages_sent', range));
  const messagesReceived = useQuery(analyticsMetricQuery(workspaceId, 'messages_received', range));
  const sessionDuration = useQuery(sessionDurationQuery(workspaceId, range));
  const failedRuns = useQuery(analyticsMetricQuery(workspaceId, 'failed_agent_runs', range));
  const kbUploads = useQuery(analyticsMetricQuery(workspaceId, 'kb_files_uploaded', range));

  const messagesPoints = toStackedPoints(messagesSent.data, messagesReceived.data);
  const messagesTotal = sum(messagesSent.data) + sum(messagesReceived.data);

  return (
    <div className="flex flex-col gap-xl">
      <div className="flex">
        <RangeToggle value={range} onChange={setRange} />
      </div>

      <div className="grid grid-cols-1 gap-grid md:grid-cols-2 xl:grid-cols-3">
        <MetricCard
          title={t('analyticsDashboard.analyticsDashboard.activeUsers')}
          description={t('analyticsDashboard.analyticsDashboard.averagePerDay')}
          value={formatDecimal(average(activeUsers.data))}
          isLoading={activeUsers.isPending}
          isError={activeUsers.isError}
          isEmpty={hasNoActivity(activeUsers.data?.values ?? [])}
        >
          <MetricChart
            data={toPoints(activeUsers.data)}
            primaryLabel={t('analyticsDashboard.analyticsDashboard.activeUsersSeries')}
          />
        </MetricCard>

        <MetricCard
          title={t('analyticsDashboard.analyticsDashboard.agentSessions')}
          description={t('analyticsDashboard.analyticsDashboard.totalOverThePeriod')}
          value={formatCount(sum(sessions.data))}
          isLoading={sessions.isPending}
          isError={sessions.isError}
          isEmpty={hasNoActivity(sessions.data?.values ?? [])}
        >
          <MetricChart
            data={toPoints(sessions.data)}
            primaryLabel={t('analyticsDashboard.analyticsDashboard.sessions')}
          />
        </MetricCard>

        <MetricCard
          title={t('analyticsDashboard.analyticsDashboard.messagesExchanged')}
          description={t('analyticsDashboard.analyticsDashboard.sentAndReceived')}
          value={formatCount(messagesTotal)}
          isLoading={messagesSent.isPending || messagesReceived.isPending}
          isError={messagesSent.isError || messagesReceived.isError}
          isEmpty={messagesTotal === 0}
        >
          <MetricChart
            data={messagesPoints}
            primaryLabel={t('analyticsDashboard.analyticsDashboard.sent')}
            secondaryLabel={t('analyticsDashboard.analyticsDashboard.received')}
          />
        </MetricCard>

        <MetricCard
          title={t('analyticsDashboard.analyticsDashboard.avgSessionDuration')}
          // Not "per session": this averages daily figures, and cannot weight
          // them by session count (see averageOverActiveDays).
          description={t('analyticsDashboard.analyticsDashboard.averageOnDaysWithActivity')}
          value={t('analyticsDashboard.analyticsDashboard.minutesValue', {
            value: formatDecimal(averageOverActiveDays(sessionDuration.data)),
          })}
          isLoading={sessionDuration.isPending}
          isError={sessionDuration.isError}
          isEmpty={hasNoActivity(sessionDuration.data?.values ?? [])}
        >
          <MetricChart
            data={toPoints(sessionDuration.data)}
            primaryLabel={t('analyticsDashboard.analyticsDashboard.minutes')}
            allowDecimals
          />
        </MetricCard>

        <MetricCard
          title={t('analyticsDashboard.analyticsDashboard.failedAgentRuns')}
          description={t('analyticsDashboard.analyticsDashboard.totalOverThePeriod')}
          value={formatCount(sum(failedRuns.data))}
          isLoading={failedRuns.isPending}
          isError={failedRuns.isError}
          isEmpty={hasNoActivity(failedRuns.data?.values ?? [])}
        >
          <MetricChart
            data={toPoints(failedRuns.data)}
            primaryLabel={t('analyticsDashboard.analyticsDashboard.failedRuns')}
          />
        </MetricCard>

        <MetricCard
          title={t('analyticsDashboard.analyticsDashboard.kbFilesUploaded')}
          description={t('analyticsDashboard.analyticsDashboard.totalOverThePeriod')}
          value={formatCount(sum(kbUploads.data))}
          isLoading={kbUploads.isPending}
          isError={kbUploads.isError}
          isEmpty={hasNoActivity(kbUploads.data?.values ?? [])}
        >
          <MetricChart
            data={toPoints(kbUploads.data)}
            primaryLabel={t('analyticsDashboard.analyticsDashboard.filesUploaded')}
          />
        </MetricCard>
      </div>
    </div>
  );
};
